import React, { useEffect, useState, useRef } from 'react'
import type { TabType } from '../components/CheckInCheckOut/CheckInMain'
import { useColorMode, Skeleton } from '@chakra-ui/react'
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore'
import { db } from '../firebase/firebase'
import { invalidateCheckInCache } from './CheckInAlert'
import { fetchNonCheckInReasonsForYear, type NonCheckInReasonEntry } from '../utils/nonCheckInReasonUtils'

type User = {
  id: string
  firstName?: string
  lastName?: string
  role?: string
  permissions?: 'Admin' | 'Editor' | 'Viewer'
  approvalStatus?: 'Yes' | 'No'
  isOrgAdmin?: boolean
}

type PendingEntry = NonCheckInReasonEntry & { _monthKey: string; _docId: string }

interface NonCheckinApprovalPageProps {
  organizationId: string
  currentUserId: string
  users: User[]
  setActiveTab?: React.Dispatch<React.SetStateAction<TabType>>
  activeSubTab?: 'timesheets' | 'leaves' | 'wfh' | 'noncheckin'
  setActiveSubTab?: React.Dispatch<React.SetStateAction<'timesheets' | 'leaves' | 'wfh' | 'noncheckin'>>
}

export default function NonCheckinApprovalPage({
  organizationId,
  currentUserId,
  users,
  setActiveTab,
  activeSubTab,
  setActiveSubTab,
}: NonCheckinApprovalPageProps) {
  const [loading, setLoading] = useState(true)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [entriesByUser, setEntriesByUser] = useState<Record<string, PendingEntry[]>>({})
  const [selectedUser, setSelectedUser] = useState<string>('ALL')
  const [isUserPickerOpen, setIsUserPickerOpen] = useState(false)
  const [userQuery, setUserQuery] = useState('')
  const [userPickerPlacement, setUserPickerPlacement] = useState<'top' | 'bottom'>('bottom')
  const userPickerRef = useRef<HTMLDivElement | null>(null)
  const [confirmState, setConfirmState] = useState<{
    open: boolean
    title: string
    description?: string
    confirmText?: string
    cancelText?: string
    onConfirm: () => Promise<void> | void
  } | null>(null)

  const me = users.find((u) => u.id === currentUserId)
  const userPermissions = me?.permissions
  const isAdmin = !!(me as any)?.isOrgAdmin || userPermissions?.toLowerCase() === 'admin' || (me?.role || '').toLowerCase() === 'admin' || (me?.approvalStatus || '').toLowerCase() === 'yes'

  const fetchPending = async () => {
    setLoading(true)
    try {
      const currentYear = new Date().getFullYear()
      const byUser: Record<string, PendingEntry[]> = {}

      // Fetch all months for the current year for each user (matches mobile app)
      await Promise.all(
        users.map(async (user) => {
          if (user.id === currentUserId) {
            return
          }
          try {
            const allEntries = await fetchNonCheckInReasonsForYear(organizationId, user.id, currentYear)
            const pendingEntries: PendingEntry[] = []
            for (const [dateKey, entry] of Object.entries(allEntries)) {
              if (entry.approvalStatus === 'Pending') {
                const [year, month] = dateKey.split('-')
                pendingEntries.push({
                  ...entry,
                  _monthKey: `${year}-${month}`,
                  _docId: dateKey,
                })
              }
            }
            if (pendingEntries.length > 0) {
              pendingEntries.sort((a, b) => {
                try {
                  return new Date(b.date).getTime() - new Date(a.date).getTime()
                } catch {
                  return 0
                }
              })
              byUser[user.id] = pendingEntries
            }
          } catch (err) {
            console.error(`Error fetching non-check-in for user ${user.id}:`, err)
          }
        })
      )

      if (!isAdmin) {
        setEntriesByUser({})
      } else {
        setEntriesByUser(byUser)
      }
    } catch (err) {
      console.error('fetchPending error:', err)
      setEntriesByUser({})
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!organizationId || !currentUserId || users.length === 0) return
    fetchPending()
  }, [organizationId, currentUserId, users])

  useEffect(() => {
    if (!selectedUser) setSelectedUser('ALL')
  }, [selectedUser])

  // Close user picker on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-user-picker]')) {
        setIsUserPickerOpen(false)
      }
    }
    if (isUserPickerOpen) {
      document.addEventListener('mousedown', onClick)
      return () => document.removeEventListener('mousedown', onClick)
    }
  }, [isUserPickerOpen])

  // Decide dropdown placement
  useEffect(() => {
    const recomputePlacement = () => {
      const el = userPickerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const desiredHeight = 240
      if (spaceBelow < desiredHeight && spaceAbove > spaceBelow) {
        setUserPickerPlacement('top')
      } else {
        setUserPickerPlacement('bottom')
      }
    }
    if (isUserPickerOpen) {
      recomputePlacement()
      window.addEventListener('resize', recomputePlacement)
      window.addEventListener('scroll', recomputePlacement, true)
      return () => {
        window.removeEventListener('resize', recomputePlacement)
        window.removeEventListener('scroll', recomputePlacement, true)
      }
    }
  }, [isUserPickerOpen])

  const displayed = selectedUser === 'ALL'
    ? Object.values(entriesByUser).flat()
    : entriesByUser[selectedUser] || []

  const userOptions = users.filter((user) => entriesByUser[user.id] && entriesByUser[user.id].length > 0)

  const updateStatus = async (userId: string, entry: PendingEntry, newStatus: 'Approved' | 'Rejected') => {
    const key = `${userId}-${entry._docId}`
    setProcessingId(key)
    try {
      const entryRef = doc(
        db,
        'organization',
        organizationId,
        'NonCheckInReason',
        userId,
        entry._monthKey,
        entry._docId
      )
      await updateDoc(entryRef, {
        approvalStatus: newStatus,
        reviewedBy: currentUserId,
        reviewedAt: new Date().toISOString(),
      })

      // If approved and auto-checkout, set markedAsAbsent to false
      if (newStatus === 'Approved' && entry.type === 'auto checkout') {
        const monthColRef = collection(
          db,
          `organization/${organizationId}/CheckInCheckOutData/${userId}/${entry._monthKey}`
        )
        const snapshot = await getDocs(monthColRef)
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data()
          if (data.checkInTime) {
            const recordDate = new Intl.DateTimeFormat('en-CA', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              timeZone: 'Asia/Kolkata',
            }).format(new Date(data.checkInEpoch || Date.parse(data.checkInTime)))
            if (recordDate === entry.date && data.markedAsAbsent === true) {
              await updateDoc(docSnap.ref, { markedAsAbsent: false })
              break
            }
          }
        }
      }

      invalidateCheckInCache()

      // Update local state
      setEntriesByUser((prev) => {
        const updated = { ...prev }
        if (updated[userId]) {
          updated[userId] = updated[userId].filter((e) => e._docId !== entry._docId)
          if (updated[userId].length === 0) delete updated[userId]
        }
        return updated
      })
    } catch (err) {
      console.error('Error updating non-checkin status:', err)
    } finally {
      setProcessingId(null)
    }
  }

  const updateAllStatus = async (newStatus: 'Approved' | 'Rejected') => {
    const targetUserIds = selectedUser === 'ALL' ? Object.keys(entriesByUser) : [selectedUser]
    setProcessingId('bulk')
    try {
      for (const uid of targetUserIds) {
        const entries = entriesByUser[uid] || []
        for (const entry of entries) {
          const entryRef = doc(
            db,
            'organization',
            organizationId,
            'NonCheckInReason',
            uid,
            entry._monthKey,
            entry._docId
          )
          await updateDoc(entryRef, {
            approvalStatus: newStatus,
            reviewedBy: currentUserId,
            reviewedAt: new Date().toISOString(),
          })

          // If approved and auto-checkout, set markedAsAbsent to false
          if (newStatus === 'Approved' && entry.type === 'auto checkout') {
            const monthColRef = collection(
              db,
              `organization/${organizationId}/CheckInCheckOutData/${uid}/${entry._monthKey}`
            )
            const snapshot = await getDocs(monthColRef)
            for (const docSnap of snapshot.docs) {
              const data = docSnap.data()
              if (data.checkInTime) {
                const recordDate = new Intl.DateTimeFormat('en-CA', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  timeZone: 'Asia/Kolkata',
                }).format(new Date(data.checkInEpoch || Date.parse(data.checkInTime)))
                if (recordDate === entry.date && data.markedAsAbsent === true) {
                  await updateDoc(docSnap.ref, { markedAsAbsent: false })
                  break
                }
              }
            }
          }
        }
      }
      invalidateCheckInCache()
      await fetchPending()
    } catch (err) {
      console.error('Error bulk updating non-checkin:', err)
    } finally {
      setProcessingId(null)
    }
  }

  const updateAllForUser = async (uid: string, newStatus: 'Approved' | 'Rejected') => {
    setProcessingId(`bulk-${uid}`)
    try {
      const entries = entriesByUser[uid] || []
      for (const entry of entries) {
        const entryRef = doc(
          db,
          'organization',
          organizationId,
          'NonCheckInReason',
          uid,
          entry._monthKey,
          entry._docId
        )
        await updateDoc(entryRef, {
          approvalStatus: newStatus,
          reviewedBy: currentUserId,
          reviewedAt: new Date().toISOString(),
        })

        if (newStatus === 'Approved' && entry.type === 'auto checkout') {
          const monthColRef = collection(
            db,
            `organization/${organizationId}/CheckInCheckOutData/${uid}/${entry._monthKey}`
          )
          const snapshot = await getDocs(monthColRef)
          for (const docSnap of snapshot.docs) {
            const data = docSnap.data()
            if (data.checkInTime) {
              const recordDate = new Intl.DateTimeFormat('en-CA', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'Asia/Kolkata',
              }).format(new Date(data.checkInEpoch || Date.parse(data.checkInTime)))
              if (recordDate === entry.date && data.markedAsAbsent === true) {
                await updateDoc(docSnap.ref, { markedAsAbsent: false })
                break
              }
            }
          }
        }
      }
      invalidateCheckInCache()
      await fetchPending()
    } catch (err) {
      console.error('Error bulk updating non-checkin for user:', err)
    } finally {
      setProcessingId(null)
    }
  }

  const confirmAndUpdateAll = (newStatus: 'Approved' | 'Rejected') => {
    const scope = selectedUser === 'ALL' ? 'all users' : (() => {
      const u = users.find((uu) => uu.id === selectedUser)
      return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || selectedUser : selectedUser
    })()
    const actionWord = newStatus === 'Approved' ? 'approve' : 'reject'
    setConfirmState({
      open: true,
      title: `Confirm ${newStatus === 'Approved' ? 'Approval' : 'Rejection'}`,
      description: `Are you sure you want to ${actionWord} all pending non-checkin reasons for ${scope}?`,
      confirmText: newStatus === 'Approved' ? 'Approve All' : 'Reject All',
      cancelText: 'Cancel',
      onConfirm: async () => {
        await updateAllStatus(newStatus)
      },
    })
  }

  const confirmAndUpdateAllForUser = (uid: string, newStatus: 'Approved' | 'Rejected') => {
    const u = users.find((uu) => uu.id === uid)
    const name = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || uid : uid
    const actionWord = newStatus === 'Approved' ? 'approve' : 'reject'
    setConfirmState({
      open: true,
      title: `Confirm ${newStatus === 'Approved' ? 'Approval' : 'Rejection'}`,
      description: `Are you sure you want to ${actionWord} all pending non-checkin reasons for ${name}?`,
      confirmText: newStatus === 'Approved' ? 'Approve All' : 'Reject All',
      cancelText: 'Cancel',
      onConfirm: async () => {
        await updateAllForUser(uid, newStatus)
      },
    })
  }

  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'

  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

  const renderTable = (entries: PendingEntry[], uid: string) => (
    <table className="min-w-full text-sm">
      <thead className={`sticky top-0 z-20 ${isDark ? 'bg-[#14161B] border-t border-[#2E2F33]' : 'bg-gray-100 border-t border-gray-200'}`}>
        <tr className="h-10">
          <th className="px-4 py-2 text-left font-semibold">Date</th>
          <th className="px-4 py-2 text-left font-semibold">Reason</th>
          <th className="px-4 py-2 text-left font-semibold">Type</th>
          <th className="px-4 py-2 text-center font-semibold">Status</th>
          <th className="px-4 py-2 text-left font-semibold">Actions</th>
        </tr>
      </thead>
      <tbody className={`${isDark ? 'divide-y divide-[#2E2F33]' : 'divide-y divide-gray-200'}`}>
        {entries.map((e) => {
          const entryKey = `${uid}-${e._docId}`
          const isLoading = processingId === entryKey
          return (
            <tr
              key={entryKey}
              className={`${isDark ? 'bg-[#1A1C1F] border-b border-[#2E2F33] hover:bg-[#2A3441]' : 'bg-white border-b border-gray-200 hover:bg-gray-50'}`}
            >
              <td className="px-4 py-2">{e.date}</td>
              <td className="px-4 py-2 truncate max-w-xs" title={e.reason}>
                {e.reason || '-'}
              </td>
              <td className="px-4 py-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  e.type === 'auto checkout'
                    ? 'bg-amber-100 text-amber-700 border border-amber-200'
                    : 'bg-slate-100 text-slate-700 border border-slate-200'
                }`}>
                  {e.type === 'auto checkout' ? 'Auto Checkout' : 'Non CheckIn'}
                </span>
              </td>
              <td className="px-4 py-2 text-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700 border border-yellow-200">
                  Pending
                </span>
              </td>
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <button
                    disabled={isLoading || processingId === 'bulk'}
                    className="relative px-2 py-1 bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C] rounded text-xs hover:bg-[#FAEDD0] disabled:opacity-50"
                    onClick={() => updateStatus(uid, e, 'Approved')}
                  >
                    <span className={isLoading ? 'invisible' : ''}>Approve</span>
                    {isLoading && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <svg className="animate-spin h-3 w-3 text-[#B59143]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      </span>
                    )}
                  </button>
                  <button
                    disabled={isLoading || processingId === 'bulk'}
                    className="relative px-2 py-1 border border-red-300 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100 disabled:opacity-50"
                    onClick={() => updateStatus(uid, e, 'Rejected')}
                  >
                    <span className={isLoading ? 'invisible' : ''}>Reject</span>
                    {isLoading && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <svg className="animate-spin h-3 w-3 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      </span>
                    )}
                  </button>
                </div>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )

  return (
    <div className="w-full h-full flex flex-col py-0 px-4 min-h-0 text-gray-900">
      {/* Header */}
      <div
        className={`flex justify-between items-center p-4 min-h-[64px] overflow-visible ${
          isDark
            ? 'relative overflow-hidden bg-gradient-to-br from-[#0B0B0F] via-[#1A1C1F] to-[#0B0B0F] ring-1 ring-[#2E2F33] shadow-[inset_0_1px_0_rgba(251,210,130,0.08),inset_0_-1px_0_rgba(251,210,130,0.05),0_10px_28px_rgba(0,0,0,0.45)]'
            : 'bg-gradient-to-r from-[#FFF7E6] via-[#FAEDD0] to-[#FFF8EA] shadow-[inset_0_-1px_0_0_rgba(229,231,235,0.7),0_2px_8px_rgba(181,145,67,0.08)]'
        } rounded-md mb-4 border ${isDark ? 'border-[#2E2F33]' : 'border-[#E5E7EB]'} flex-shrink-0`}
      >
        {isDark && (
          <>
            <span className="pointer-events-none absolute inset-0 [background:radial-gradient(120%_60%_at_50%_120%,rgba(181,145,67,0.18)_0%,rgba(0,0,0,0)_60%)]" />
            <span className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FBD282]/35 to-transparent" />
            <span className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#B59143]/25 to-transparent blur-[2px]" />
            <span className="pointer-events-none absolute -left-10 top-1/2 h-24 w-1/2 -translate-y-1/2 -rotate-6 bg-gradient-to-r from-transparent via-[#FBD282]/14 to-transparent blur-sm" />
          </>
        )}
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-0">
            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Approvals</span>
            <span className={`text-xs ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>
              Review and approve pending non-checkin reasons.
            </span>
          </div>
        </div>
        {/* Tab Switcher */}
        {setActiveSubTab && (
          <div
            role="tablist"
            aria-label="Approval type"
            className={`relative grid grid-cols-4 ${
              isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white/80 border-gray-200'
            } border rounded-full p-1.5 shadow-sm`}
          >
            <span
              className={`absolute top-1.5 bottom-1.5 rounded-full transition-all duration-300 ${
                isDark ? 'bg-[#2A3441]' : 'bg-[#FFF7E6] border border-[#E8C16C]'
              } shadow-sm`}
              style={
                activeSubTab === 'timesheets'
                  ? { left: '0.375rem', right: '75%' }
                  : activeSubTab === 'leaves'
                  ? { left: '25%', right: '50%' }
                  : activeSubTab === 'wfh'
                  ? { left: '50%', right: '25%' }
                  : { left: '75%', right: '0.375rem' }
              }
              aria-hidden="true"
            />
            <button
              role="tab"
              aria-selected={activeSubTab === 'timesheets'}
              onClick={() => setActiveSubTab('timesheets')}
              className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${
                activeSubTab === 'timesheets'
                  ? isDark ? 'text-white' : 'text-gray-900'
                  : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
              }`}
            >
              Timesheets
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'leaves'}
              onClick={() => setActiveSubTab('leaves')}
              className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${
                activeSubTab === 'leaves'
                  ? isDark ? 'text-white' : 'text-gray-900'
                  : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
              }`}
            >
              Leaves
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'wfh'}
              onClick={() => setActiveSubTab('wfh')}
              className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${
                activeSubTab === 'wfh'
                  ? isDark ? 'text-white' : 'text-gray-900'
                  : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
              }`}
            >
              Remote Work
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'noncheckin'}
              onClick={() => setActiveSubTab('noncheckin')}
              className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${
                activeSubTab === 'noncheckin'
                  ? isDark ? 'text-white' : 'text-gray-900'
                  : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
              }`}
            >
              Non Checkins
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className={`max-w-full rounded-md ${
          isDark ? 'border-[#2E2F33] bg-[#1A1C1F] text-white' : 'border-gray-200 bg-white'
        } border flex-1 min-h-0 flex flex-col`}
      >
        {loading ? (
          <div className="p-6 flex-1 min-h-0 overflow-y-auto">
            <div className={`rounded-md overflow-hidden border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}>
                    <tr className="h-10">
                      <th className="px-4 py-3 text-left font-semibold">
                        <Skeleton h="16px" w="80px" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        <Skeleton h="16px" w="150px" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        <Skeleton h="16px" w="80px" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        <Skeleton h="16px" w="50px" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        <Skeleton h="16px" w="80px" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? 'divide-y divide-[#2E2F33]' : 'divide-y divide-gray-200'}`}>
                    {[1, 2, 3, 4, 5].map((rowIdx) => (
                      <tr key={rowIdx} className={`h-12 ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'}`}>
                        <td className="px-4 py-3">
                          <Skeleton h="14px" w="80px" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                        </td>
                        <td className="px-4 py-3">
                          <Skeleton h="14px" w="150px" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                        </td>
                        <td className="px-4 py-3">
                          <Skeleton h="14px" w="80px" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Skeleton h="14px" w="50px" m="auto" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Skeleton h="24px" w="55px" borderRadius="sm" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                            <Skeleton h="24px" w="50px" borderRadius="sm" startColor={isDark ? 'gray.700' : 'gray.200'} endColor={isDark ? 'gray.600' : 'gray.300'} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : displayed.length === 0 ? (
          <div className="p-8 flex-1 min-h-0">
            <div className={`flex justify-center items-center h-[40vh] ${isDark ? 'text-white' : 'text-gray-900'}`}>
              No pending non-checkin reasons.
            </div>
          </div>
        ) : (
          <>
            {/* Action bar */}
            <div className="flex items-center justify-between gap-3 px-6 pt-2 mb-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <button
                  className={`h-9 px-3 rounded-md text-sm flex items-center gap-1 border transition-colors ${
                    displayed.length === 0
                      ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                      : 'border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]'
                  }`}
                  onClick={() => confirmAndUpdateAll('Approved')}
                  disabled={displayed.length === 0 || processingId === 'bulk'}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Approve All
                </button>
                <button
                  className={`h-9 px-3 rounded-md text-sm flex items-center gap-1 border transition-colors ${
                    displayed.length === 0
                      ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                      : 'border-red-300 text-red-600 hover:bg-red-50'
                  }`}
                  onClick={() => confirmAndUpdateAll('Rejected')}
                  disabled={displayed.length === 0 || processingId === 'bulk'}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Reject All
                </button>
              </div>
              {/* User Picker */}
              <div className="relative flex-shrink-0 w-48" data-user-picker ref={userPickerRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsUserPickerOpen((o) => !o)
                    setUserQuery('')
                  }}
                  className={`w-full h-9 flex items-center gap-2 rounded-md border ${
                    isDark ? 'border-[#2E2F33] bg-[#14161B] text-white' : 'border-gray-300 bg-white text-gray-800'
                  } px-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#B59143] text-left`}
                >
                  <svg
                    className="w-4 h-4 text-gray-500 flex-shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="7"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <span className={`flex-1 text-sm truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {selectedUser === 'ALL'
                      ? 'All Users'
                      : (() => {
                          const u = users.find((u) => u.id === selectedUser)
                          return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.id : selectedUser
                        })()}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${isUserPickerOpen ? 'rotate-180' : 'rotate-0'}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {isUserPickerOpen && (
                  <div
                    className={`fixed z-[9999] w-48 rounded-lg border ${
                      isDark ? 'border-[#2E2F33] bg-[#1A1C1F]' : 'border-gray-200 bg-white'
                    } shadow-lg`}
                    style={(() => {
                      const btn = userPickerRef.current?.querySelector('button')
                      if (!btn) return { top: 0, left: 0 }
                      const r = btn.getBoundingClientRect()
                      return userPickerPlacement === 'top'
                        ? { bottom: window.innerHeight - r.top + 4, left: r.left, width: r.width }
                        : { top: r.bottom + 4, left: r.left, width: r.width }
                    })()}
                  >
                    <div
                      className={`sticky top-0 ${
                        isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-100'
                      } border-b p-2`}
                    >
                      <input
                        autoFocus
                        value={userQuery}
                        onChange={(e) => setUserQuery(e.target.value)}
                        placeholder="Search user..."
                        className={`w-full rounded-md border ${
                          isDark
                            ? 'border-[#2E2F33] bg-[#14161B] text-white placeholder-gray-400'
                            : 'border-gray-300 bg-white text-gray-900'
                        } px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#B59143]`}
                      />
                    </div>
                    <div className="max-h-60 overflow-auto">
                      <button
                        className={`w-full text-left px-3 py-2 text-sm ${
                          isDark ? 'text-gray-200' : 'text-gray-800'
                        } ${selectedUser === 'ALL' ? 'bg-[#FFF7E6] !text-black cursor-default' : 'hover:bg-[#FFF7E6] hover:text-gray-900'}`}
                        aria-selected={selectedUser === 'ALL'}
                        onClick={() => {
                          setSelectedUser('ALL')
                          setIsUserPickerOpen(false)
                        }}
                      >
                        All Users
                      </button>
                      {userOptions
                        .filter((u) =>
                          `${u.firstName || ''} ${u.lastName || ''}`
                            .toLowerCase()
                            .includes(userQuery.toLowerCase())
                        )
                        .map((u) => (
                          <button
                            key={u.id}
                            className={`w-full text-left px-3 py-2 text-sm ${
                              isDark ? 'text-gray-200' : 'text-gray-800'
                            } ${selectedUser === u.id ? 'bg-[#FFF7E6] !text-black cursor-default' : 'hover:bg-[#FFF7E6] hover:text-gray-900'}`}
                            aria-selected={selectedUser === u.id}
                            onClick={() => {
                              setSelectedUser(u.id)
                              setIsUserPickerOpen(false)
                            }}
                          >
                            {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.id}
                          </button>
                        ))}
                      {userOptions.filter((u) =>
                        `${u.firstName || ''} ${u.lastName || ''}`
                          .toLowerCase()
                          .includes(userQuery.toLowerCase())
                      ).length === 0 && (
                        <div className={`px-3 py-2 text-sm ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                          No users found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              {selectedUser === 'ALL' ? (
                <div className="px-6 pb-2 space-y-6">
                  {Object.keys(entriesByUser).map((uid) => {
                    const user = users.find((u) => u.id === uid)
                    const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || uid
                    const items = entriesByUser[uid] || []
                    if (items.length === 0) return null
                    return (
                      <div
                        key={uid}
                        className={`rounded-md overflow-hidden border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}
                      >
                        <div
                          className={`flex items-center justify-between px-4 py-2 ${
                            isDark ? 'bg-[#14161B]' : 'bg-gray-50'
                          }`}
                        >
                          <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {name}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              disabled={processingId === 'bulk'}
                              className="h-8 px-3 rounded-md text-xs flex items-center gap-1 border border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6] disabled:opacity-50"
                              onClick={() => confirmAndUpdateAllForUser(uid, 'Approved')}
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              Approve All
                            </button>
                            <button
                              disabled={processingId === 'bulk'}
                              className="h-8 px-3 rounded-md text-xs flex items-center gap-1 border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                              onClick={() => confirmAndUpdateAllForUser(uid, 'Rejected')}
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              Reject All
                            </button>
                          </div>
                        </div>
                        <div className="overflow-x-auto max-h-[350px] overflow-y-auto relative">
                          {renderTable(items, uid)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="overflow-x-auto px-6 pb-2">
                  {renderTable(displayed, selectedUser)}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmState?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setConfirmState(null)} />
          <div
            className={`relative rounded-lg shadow-xl border w-full max-w-md mx-4 p-5 ${
              isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-200'
            }`}
          >
            <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {confirmState.title}
            </h3>
            {confirmState.description && (
              <p className={`text-sm mb-4 ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>
                {confirmState.description}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                className={`px-3 py-1.5 rounded-md border ${
                  isDark
                    ? 'border-[#2E2F33] text-gray-300 hover:bg-[#2A3441]'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setConfirmState(null)}
              >
                {confirmState.cancelText || 'Cancel'}
              </button>
              <button
                className="px-3 py-1.5 rounded-md border border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]"
                onClick={async () => {
                  const fn = confirmState.onConfirm
                  setConfirmState(null)
                  await fn()
                }}
              >
                {confirmState.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
